import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useLanguage } from '../contexts/LanguageContext'

const Navbar = () => {
  const { t, toggleLanguage, language, getLangPrefix } = useLanguage()
  const langPrefix = getLangPrefix()
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [mobileMenuOpen])

  const navLinks = [
    { href: '#features', label: t.nav.features },
    { href: '#how-it-works', label: t.nav.howItWorks },
    { href: '#pricing', label: t.nav.pricing },
    { href: '#community', label: t.nav.community },
  ]

  const scrollToSection = (e, href) => {
    e.preventDefault()
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'glass-effect shadow-lg border-b border-primary/10 bg-white/95 backdrop-blur-md'
          : 'bg-transparent md:bg-transparent'
      }`}
      style={{ position: 'relative' }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-14 md:h-16 lg:h-20">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 md:gap-3"
          >
            <Link to={langPrefix} className="flex items-center gap-2 md:gap-3" onClick={() => setMobileMenuOpen(false)}>
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className="w-8 h-8 md:w-10 md:h-10"
              >
                <svg viewBox="0 0 32 32" fill="none" className="w-full h-full">
                  <circle cx="16" cy="16" r="14" fill="url(#logo-gradient)" />
                  <path
                    d="M16 8C16 8 12 12 12 16C12 18.2091 13.7909 20 16 20C18.2091 20 20 18.2091 20 16C20 12 16 8 16 8Z"
                    fill="white"
                  />
                  <circle cx="16" cy="22" r="1.5" fill="white" />
                  <defs>
                    <linearGradient
                      id="logo-gradient"
                      x1="2"
                      y1="2"
                      x2="30"
                      y2="30"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#10b981" />
                      <stop offset="1" stopColor="#059669" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>
              <span className="text-lg md:text-xl font-bold gradient-text">Juvenis</span>
            </Link>
          </motion.div>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, index) => (
              <motion.a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
                whileHover={{ y: -2 }}
                className="text-gray-600 hover:text-primary font-medium transition-colors relative group"
              >
                {link.label}
                <motion.span
                  className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"
                  initial={false}
                />
              </motion.a>
            ))}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <motion.button
                onClick={() => {
                  if (language !== 'ru') {
                    toggleLanguage()
                  }
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-colors ${
                  language === 'ru'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                aria-label="Russian language"
              >
                RU
              </motion.button>
              <motion.button
                onClick={() => {
                  if (language !== 'en') {
                    toggleLanguage()
                  }
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-colors ${
                  language === 'en'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                aria-label="English language"
              >
                EN
              </motion.button>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="hidden sm:block"
            >
              <Link
                to={`${langPrefix}/waitlist`}
                className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 gradient-bg text-white rounded-lg md:rounded-xl font-semibold text-sm sm:text-base md:text-lg shadow-lg hover:shadow-xl transition-all duration-300 premium-glow hover:premium-glow"
              >
                {t.nav.startFree}
              </Link>
            </motion.div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-primary transition-colors"
              aria-label="Toggle menu"
            >
              <motion.svg
                animate={{ rotate: mobileMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </motion.svg>
            </button>
          </div>
        </div>
      </div>

      <motion.div
        initial={false}
        animate={{
          height: mobileMenuOpen ? 'auto' : 0,
          opacity: mobileMenuOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="lg:hidden overflow-hidden w-full absolute top-full left-0 right-0 z-50"
      >
        <div className="py-4 space-y-2 border-t border-primary/10 bg-white/95 backdrop-blur-md shadow-xl">
          {navLinks.map((link, index) => (
            <motion.a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                scrollToSection(e, link.href)
                setMobileMenuOpen(false)
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: mobileMenuOpen ? 1 : 0,
                x: mobileMenuOpen ? 0 : -20,
              }}
              transition={{ delay: index * 0.05 }}
              className="block px-6 py-3 text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-emerald-50 hover:to-primary/5 font-medium transition-all duration-200"
            >
              {link.label}
            </motion.a>
          ))}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{
              opacity: mobileMenuOpen ? 1 : 0,
              x: mobileMenuOpen ? 0 : -20,
            }}
            transition={{ delay: navLinks.length * 0.05 }}
            className="px-6 pt-2 pb-4"
          >
            <Link
              to={`${langPrefix}/waitlist`}
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-center px-4 py-3 gradient-bg text-white rounded-xl font-semibold shadow-lg transition-all duration-300 premium-glow"
            >
              {t.nav.startFree}
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </motion.nav>
  )
}

export default Navbar
